import React, { useEffect, useState } from "react";
import { Share, Plus, MoreVertical, Smartphone, Download } from "lucide-react";
import { SectionTitle, Card } from "../components/common";

export default function InstallPage() {
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); };
    const onInstalled = () => setInstalled(true);
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => { window.removeEventListener("beforeinstallprompt", onPrompt); window.removeEventListener("appinstalled", onInstalled); };
  }, []);

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    await deferred.userChoice;
    setDeferred(null);
  };

  const Step = ({ n, icon, children }) => (
    <div className="flex items-start gap-3 py-2" >
      <div className="w-7 h-7 rounded-full bg-green text-cream text-sm font-black flex items-center justify-center shrink-0">{n}</div>
      <div className="flex items-center gap-2 text-sm text-ink/80">{icon}<span>{children}</span></div>
    </div>
  );

  return (
    <div className="max-w-3xl mx-auto" data-testid="install-page">
      <SectionTitle kicker="Add to your phone">Install the PSL app</SectionTitle>
      <div className="p-5 mb-5 rounded-sm text-cream flex items-center gap-4" style={{ backgroundColor: "#E40119" }}>
        <img src="/icon-192.png" alt="PSL" className="w-16 h-16 rounded-xl shrink-0" />
        <div>
          <div className="font-head font-black text-lg">Castle Lager PSL 2026</div>
          <p className="text-sm text-cream/90">Get the league on your home screen — full-screen, fast, and free. It's a web app: no App Store or Play Store needed, and it always stays up to date.</p>
        </div>
      </div>

      {deferred && !installed && (
        <button onClick={install} className="w-full mb-5 py-3 bg-gold text-ink font-black rounded-sm flex items-center justify-center gap-2" data-testid="install-button">
          <Download size={18} /> Install now
        </button>
      )}
      {installed && <div className="mb-5 p-3 rounded-sm bg-green/10 text-green text-sm font-bold text-center">Installed! Look for the PSL icon on your home screen.</div>}

      <div className="grid sm:grid-cols-2 gap-4">
        <Card className="p-5" data-testid="ios-instructions">
          <div className="flex items-center gap-2 mb-3 font-head font-black text-green"><Smartphone size={18} /> iPhone &amp; iPad (Safari)</div>
          <Step n="1" icon={<Share size={16} className="text-green" />}>Tap the <b>Share</b> button in Safari's toolbar.</Step>
          <Step n="2" icon={<Plus size={16} className="text-green" />}>Scroll down and tap <b>Add to Home Screen</b>.</Step>
          <Step n="3">Tap <b>Add</b> — the PSL icon appears on your home screen.</Step>
          <p className="text-[11px] text-ink/40 mt-2">Must be opened in Safari (not Chrome) on iOS.</p>
        </Card>
        <Card className="p-5" data-testid="android-instructions">
          <div className="flex items-center gap-2 mb-3 font-head font-black text-green"><Smartphone size={18} /> Android (Chrome)</div>
          <Step n="1" icon={<MoreVertical size={16} className="text-green" />}>Tap the <b>⋮</b> menu (top-right) in Chrome.</Step>
          <Step n="2" icon={<Download size={16} className="text-green" />}>Tap <b>Install app</b> (or <b>Add to Home screen</b>).</Step>
          <Step n="3">Confirm <b>Install</b> — PSL is added to your apps.</Step>
          <p className="text-[11px] text-ink/40 mt-2">If you see the Install button above, you can tap that instead.</p>
        </Card>
      </div>
    </div>
  );
}
